import React, { useEffect, useState, useCallback } from 'react';
import '../styles/cookie-consent.scss';
/*
  ================================================
  CookieConsent Component
  ================================================
  Uso actual:
    - Gestiona consentimiento para cookies ANALÍTICAS (Google Analytics 4) únicamente.
    - No se cargan scripts de analítica hasta que el usuario pulsa "Aceptar".
    - La decisión se guarda en localStorage bajo la clave STORAGE_KEY (versionada).

  Cómo añadir una nueva categoría de cookies (ej.: marketing):
    1. Añade un nuevo estado booleano o extiende "decision" a un objeto (p.ej. {analytics:true, marketing:false}).
    2. Actualiza la persistencia (función persist) para guardar ese objeto.
    3. Crea una función load<Nombre>Script() similar a loadGAScript() que inyecte los scripts SOLO si se aceptan.
    4. Amplía el banner inicial y el panel de preferencias con el nuevo toggle/botón.
    5. Actualiza la página `politica-de-cookies.js` documentando la nueva categoría antes de desplegar.
    6. Versión nueva: cambia STORAGE_KEY (ej. 'cookieConsent.v2') para forzar a los usuarios a volver a elegir.

  Nota accesibilidad:
    - El banner usa role="dialog" y aria-modal para mayor compatibilidad.
    - Si se añade complejidad, considerar manejo de foco inicial y retorno de foco.

  Para borrar la decisión durante pruebas:
    localStorage.removeItem('cookieConsent.v1') en la consola del navegador y recarga.
*/

// Keys for localStorage
const STORAGE_KEY = 'cookieConsent.v1'; // version it if text changes

const GA_ID = 'G-86TSGVMW9K';

function loadGAScript() {
  if (window.gtag || document.getElementById('ga-script')) return;
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  script.id = 'ga-script';
  document.head.appendChild(script);
  window.dataLayer = window.dataLayer || [];
  function gtag(){window.dataLayer.push(arguments);} // eslint-disable-line
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', GA_ID, { anonymize_ip: true });
}

const SHOW_MANAGE_BUTTON = true; // Cambia a false si no quieres ni siquiera el botón tras decidir

const CookieConsent = () => {
  const [decision, setDecision] = useState(null); // null | 'accept' | 'reject'
  const [openPrefs, setOpenPrefs] = useState(false);
  const [mounted, setMounted] = useState(false); // animación entrada

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.decision) {
          setDecision(parsed.decision);
          if (parsed.decision === 'accept') loadGAScript();
        }
      }
    } catch(e){/* noop */}
    const t = setTimeout(()=>setMounted(true), 15);
    return ()=>clearTimeout(t);
  }, []);

  const persist = useCallback((value) => {
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ decision: value, ts: Date.now() }));
      }
    } catch (e) {
      console.warn('CookieConsent: error guardando localStorage', e);
    }
  }, []);

  const handleAccept = () => {
    setDecision('accept');
    setOpenPrefs(false);
    persist('accept');
    loadGAScript();
  };
  const handleReject = () => {
    setDecision('reject');
    setOpenPrefs(false);
    persist('reject');
  };
  const handleChangeMind = () => {
    setOpenPrefs(true);
  };
  const closePrefs = () => setOpenPrefs(false);

  // Limpieza defensiva: si existe alguna capa .cookie-banner después de decidir y no estamos en preferencias, la eliminamos.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (decision && !openPrefs) {
      // Pequeño timeout para permitir que React desmonte primero su nodo
      const t = setTimeout(() => {
        document.querySelectorAll('.cookie-banner').forEach(n => {
          // Por si quedó por animación anterior
          n.setAttribute('aria-hidden','true');
          n.style.pointerEvents = 'none';
          n.style.display = 'none';
          // Eliminamos completamente para no dejar overlay
          if (n.parentNode) n.parentNode.removeChild(n);
        });
      }, 30);
      return () => clearTimeout(t);
    }
  }, [decision, openPrefs]);

  // Si ya hay decisión y no se están viendo preferencias:
  if (decision && !openPrefs) {
    if (!SHOW_MANAGE_BUTTON) return null;
    return (
      <div style={{position:'fixed',left:'10px',bottom:'45px',zIndex:1,display:'flex',flexDirection:'column',alignItems:'flex-start',gap:'6px'}}>
        <img src="/no-permanencia.png" alt="Sin permanencia" style={{width:'80px',height:'auto',display:'block',pointerEvents:'none'}} />
        <button
          aria-label="Gestionar cookies"
          className="cookie-manage-btn"
          onClick={handleChangeMind}
          style={manageBtnStyle}
        >Cookies</button>
      </div>
    );
  }

  if (decision && openPrefs) {
    return (
      <div className={`cookie-banner ${mounted ? 'cookie-banner--visible' : ''}`} role="dialog" aria-modal="true" aria-labelledby="cookie-title" style={bannerStyle}>
        <div className="cookie-banner__inner cookie-banner__inner--prefs" style={innerWrapStyle}>
          <h3 id="cookie-title" style={{marginTop:0}}>Preferencias de Cookies</h3>
          <p style={pStyle}>Solo usamos cookies analíticas de Google para medir el uso del sitio. Puedes activarlas o mantener solo las esenciales (técnicas).</p>
          <div style={buttonsRow}>
            <button onClick={handleAccept} style={primaryBtn}>Aceptar analíticas</button>
            <button onClick={handleReject} style={secondaryBtn}>Solo esenciales</button>
            <button onClick={closePrefs} style={ghostBtn}>Cerrar</button>
          </div>
          <small style={{opacity:.7}}>ID GA: {GA_ID}</small>
          <small style={{opacity:.7,display:'block',marginTop:'4px'}}>Más detalles en la <a href="/politica-de-cookies" style={{color:'#ff5a73'}}>Política de Cookies</a>.</small>
        </div>
      </div>
    );
  }

  // Initial banner (no decision)
  return (
    <>
      <div className={`cookie-banner ${mounted ? 'cookie-banner--visible' : ''}`} role="dialog" aria-modal="true" aria-labelledby="cookie-title" style={bannerStyle}>
        <div className="cookie-banner__inner" style={innerWrapStyle}>
        <h3 id="cookie-title" style={{marginTop:0}}>Este sitio usa cookies</h3>
        <p style={pStyle}>Utilizamos únicamente cookies técnicas necesarias y, de forma opcional, cookies analíticas (Google Analytics) para mejorar nuestros servicios. No usamos cookies publicitarias ni de perfilado. Puedes aceptarlas o rechazarlas. Siempre podrás cambiar tu elección desde el botón "Cookies".</p>
        <ul style={{margin:'0 0 1rem 1.2rem',padding:0,fontSize:'13px',lineHeight:'18px'}}>
          <li><strong>Necesarias:</strong> funcionamiento básico del sitio (no almacenan datos personales).</li>
          <li><strong>Analíticas (opcional):</strong> medición anónima de visitas (Google Analytics con IP anonimizada).</li>
        </ul>
        <div style={buttonsRow}>
          <button onClick={handleAccept} style={primaryBtn}>Aceptar todas</button>
          <button onClick={handleReject} style={secondaryBtn}>Rechazar analíticas</button>
        </div>
        <small style={{opacity:.7}}>Puedes revisar más detalles en la <a href="/politica-de-cookies" style={{color:'#ff5a73'}}>Política de Cookies</a>.</small>
      </div>
      </div>
      {/* Imagen + botón fijos mientras no se haya decidido (para consistencia visual) */}
  <div style={{position:'fixed',left:'10px',bottom:'10px',zIndex:1,display:'flex',flexDirection:'column',alignItems:'flex-start',gap:'6px'}}>
        <img src="/no-permanencia.png" alt="Sin permanencia" style={{width:'90px',height:'auto',display:'block',pointerEvents:'none'}} />
        <button
          aria-label="Gestionar cookies"
          className="cookie-manage-btn"
          onClick={handleChangeMind}
          style={manageBtnStyle}
        >Cookies</button>
      </div>
    </>
  );
};

// Inline styles (could be moved to SCSS if desired)
const bannerStyle = {
  position:'fixed',
  bottom:0,
  left:0,
  right:0,
  background:'rgba(0,0,0,0.95)',
  color:'#f1f1f1',
  padding:'18px 24px',
  zIndex:2147483600, // super alto
  boxShadow:'0 -2px 10px rgba(0,0,0,.4)',
  fontFamily:'Lato, sans-serif',
  fontSize:'14px'
};
const pStyle = {margin:'0 0 12px'};
const buttonsRow = {display:'flex',gap:'10px',flexWrap:'wrap',marginBottom:'8px'};
const baseBtn = {cursor:'pointer',borderRadius:'4px',border:'1px solid #444',fontSize:'13px',fontFamily:'inherit',padding:'8px 14px'};
const primaryBtn = {...baseBtn, background:'#AE0F2B', color:'#fff', border:'1px solid #AE0F2B'};
const secondaryBtn = {...baseBtn, background:'#222', color:'#fff'};
const ghostBtn = {...baseBtn, background:'transparent',color:'#ccc'};
const innerWrapStyle = {position:'relative',zIndex:2147483601,pointerEvents:'auto'};
const manageBtnStyle = {position:'fixed',bottom:'10px',left:'10px',background:'#111',color:'#fff',border:'1px solid #444',fontSize:'11px',padding:'4px 8px',borderRadius:'4px',cursor:'pointer',zIndex:2147483600,display:'inline-block'};

export default CookieConsent;
